import { ReactElement, useState } from 'react';
import { HiOutlineChevronRight } from 'react-icons/hi2';
import { SessionAnalysisType } from '../../../../types/SessionAnalysisType';
import { dynamicPressureSource } from '../../../../utils/effectiveCalibration';
import {
  CalibrationSeverity,
  SEVERITY_CONFIG,
  getAutocalCue,
  getCalibrationBarSeverity,
  getConfigsWithBranch,
} from './calibrationSeverity';

const STATUS_SHOW_KEY = 'session-status-show';

// Short label shown after "Session Status –" for each severity.
const STATUS_LABEL: Record<CalibrationSeverity, string> = {
  [CalibrationSeverity.GRAY]: 'No checks run yet',
  [CalibrationSeverity.GREEN]: 'All good',
  [CalibrationSeverity.YELLOW]: 'Warning, expand for details',
  [CalibrationSeverity.RED]: 'Error, expand for details',
};

interface SessionStatusBarProps {
  session: any;
}

// Collapsible status box shown above the config list. For now it simply tracks
// the calibration bar's severity/colour, with one extra rule layered on top:
// a velodrome session with no velodrome route selected is always an error.
const SessionStatusBar = ({ session }: SessionStatusBarProps): ReactElement => {
  const [show, setShow] = useState<boolean>(() => {
    const stored = localStorage.getItem(STATUS_SHOW_KEY);
    return stored !== null ? JSON.parse(stored) : false;
  });

  const isVelodromeSession =
    session?.analysis_type === SessionAnalysisType.LOOP_VELODROME_INDOORS ||
    session?.analysis_type === SessionAnalysisType.LOOP_VELODROME_OUTDOORS;
  // For velodrome sessions the selected velodrome is the session route.
  const velodromeMissing = isVelodromeSession && !session?.route_id;

  // Dynamic-pressure provenance across the session's processed runs: all
  // synthetic, or a measured/synthetic hybrid, is worth calling out. All
  // measured is the normal case and stays silent.
  const allRuns = (session?.configs ?? []).flatMap((c: any) => c?.runs || []);
  const dpSources = allRuns.map(dynamicPressureSource).filter(Boolean);
  const hasSynthetic = dpSources.includes('synthetic');
  const dpNote = hasSynthetic
    ? dpSources.includes('measured')
      ? 'Hybrid Measured / Synthetic Dynamic Pressure'
      : 'Synthetic Dynamic Pressure'
    : null;

  // Stale-solve detection: does any run's solve disagree with the CURRENT
  // force_synthetic_dp setting (run parser_vars override > session)? Two
  // directions: flag on but the run still solved measured, or flag off but
  // the run's measured DP was force-replaced (solve.selections
  // .dynamic_pressure.forced — organic no-probe synthetic doesn't count).
  // A run with no wind data (run_health_obj.wind_fallback) is synthetic
  // either way, but the flag decides whether its health warns, so it is
  // stale when the stored acceptance no longer matches the flag.
  const resolvedForceFlag = (run: any): boolean =>
    run?.parser_vars?.atmosphere?.force_synthetic_dp ??
    session?.parser_vars?.atmosphere?.force_synthetic_dp ??
    false;
  // Direction matters for the fix: synthetic → measured needs TWO
  // reprocesses (first regenerates parameters / autocalibrates, second runs
  // the calculation with them); measured → synthetic needs one.
  let dpStaleToSynthetic = 0;
  let dpStaleToMeasured = 0;
  allRuns.forEach((run: any) => {
    const source = dynamicPressureSource(run);
    if (!source) return;
    const wantForced = resolvedForceFlag(run);
    const wasForced = run?.solve?.selections?.dynamic_pressure?.forced === true;
    const windFallback = run?.run_health_obj?.wind_fallback;
    if (wantForced && source === 'measured') dpStaleToSynthetic += 1;
    else if (!wantForced && wasForced) dpStaleToMeasured += 1;
    else if (windFallback && !windFallback.still_air && (windFallback.accepted === true) !== wantForced) {
      dpStaleToSynthetic += 1;
    }
  });
  const dpStaleCount = dpStaleToSynthetic + dpStaleToMeasured;

  // Base severity tracks the calibration bar exactly.
  const calBar = getCalibrationBarSeverity(getConfigsWithBranch(session));

  // A velodrome session with no velodrome overrides everything else to an error.
  const severity = velodromeMissing ? CalibrationSeverity.RED : calBar.severity;
  // Stale synthetic-DP solves turn the bar orange (errors still win).
  const dpStale = dpStaleCount > 0 && severity !== CalibrationSeverity.RED;
  const severityConfig = dpStale
    ? { ...SEVERITY_CONFIG[severity], bg: '#ffedd5', border: '#ea580c' }
    : SEVERITY_CONFIG[severity];

  // Scope recommendation: individual runs, or the whole session when more
  // than half its processed runs are stale.
  const dpStaleScope = dpStaleCount > dpSources.length / 2
    ? 'the entire session'
    : `the affected run${dpStaleCount === 1 ? '' : 's'}`;
  const dpStaleTwice = dpStaleToMeasured > 0
    ? ' TWICE (first pass regenerates parameters and autocalibrates, second runs the calculation)'
    : '';
  const dpStaleNote = dpStaleCount > 0
    ? `${dpStaleCount} run${dpStaleCount === 1 ? '' : 's'} out of date with the `
      + `Force Synthetic Dynamic Pressure setting — reprocess ${dpStaleScope}${dpStaleTwice}`
    : null;
  // Autocalibrate state from the server (testsessions.autocal_*). Up to date
  // stays out of the header; the expanded detail shows the last-run time.
  const { note: autocalNote, failed: autocalFailed } = getAutocalCue(session);
  // autocal_ts is naive UTC from the server — pin the zone before parsing.
  const autocalTsRaw = session?.autocal_ts;
  const autocalTs = autocalTsRaw
    ? new Date(/[Zz]|[+-]\d{2}:?\d{2}$/.test(autocalTsRaw) ? autocalTsRaw : `${autocalTsRaw}Z`)
    : null;
  const autocalDetail = autocalNote
    ?? (autocalTs ? `Autocal last completed ${autocalTs.toLocaleString()}` : null);

  const label = velodromeMissing ? 'No velodrome selected' : STATUS_LABEL[severity];
  const detail = (velodromeMissing
    ? 'This is a velodrome session but no velodrome has been selected. '
      + 'Choose a velodrome in the Calibration Parameters panel below.'
    : calBar.tooltip)
    + (dpStaleNote ? `\n${dpStaleNote}.` : '')
    + (autocalDetail ? `\n${autocalDetail}.` : '');

  const toggle = () => {
    setShow((prev) => {
      const next = !prev;
      localStorage.setItem(STATUS_SHOW_KEY, JSON.stringify(next));
      return next;
    });
  };

  return (
    <div
      className="cr-mb-4 cr-rounded-lg cr-border-2 cr-border-solid cr-shadow-md cr-transition-colors cr-duration-300"
      style={{ backgroundColor: severityConfig.bg, borderColor: severityConfig.border }}
    >
      <div
        className="cr-flex cr-cursor-pointer cr-flex-row cr-items-center cr-gap-2 cr-px-5 cr-py-3"
        title={detail}
        onClick={toggle}
      >
        <HiOutlineChevronRight
          className={`cr-size-5 cr-stroke-[3px] cr-text-blue-900
                  cr-transition-transform cr-duration-[300ms] ${show && 'cr-rotate-90'}`}
        />
        <div
          className="cr-grow cr-text-center cr-font-main cr-text-base cr-font-semibold"
          style={{ color: '#03365D' }}
        >
          Session Status –{' '}
          <span className="cr-italic cr-font-normal" style={{ fontSize: '0.9em' }}>
            {label}
            {dpNote && ` · ${dpNote}`}
            {dpStaleNote && (
              <span className="cr-font-semibold" style={{ color: '#ea580c' }}>
                {` · ${dpStaleNote}`}
              </span>
            )}
            {autocalNote && (
              <span
                className={autocalFailed ? 'cr-font-semibold' : undefined}
                style={autocalFailed ? { color: '#ea580c' } : undefined}
              >
                {` · ${autocalNote}`}
              </span>
            )}
          </span>
        </div>
      </div>
      {show && (
        <div
          className="cr-rounded-b-lg cr-border-t-2 cr-border-solid cr-bg-white cr-px-5 cr-py-3"
          style={{ borderColor: severityConfig.border }}
        >
          <p className="cr-whitespace-pre-line cr-text-sm" style={{ color: '#03365D' }}>
            {detail}
          </p>
        </div>
      )}
    </div>
  );
};

export default SessionStatusBar;
